import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TablePagination,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { errorTypes } from '@/helpers/constants';
import useApiRequest from '@/hooks/useApiRequest';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useToastContext from '@/hooks/useToastContext';
import usersServices from '@/services/usersServices';
import { User } from '@/types/User';
import InputText from '../UI/InputText/InputText';
import TableList from '../UI/TableList/TableList';
import UserRow from '../UI/TableList/UserRow/UserRow';
import styles from './DialogUsers.module.scss';

const COLUMNS_DEFAULTS_FILTERS = {
  lastName: '',
  firstName: '',
  department: '',
  userCode: '',
  allFields: '',
};

const useStyles = makeStyles(() => ({
  rootDialog: {
    maxWidth: '100%',
    width: '100%',
  },
  content: {
    paddingTop: 0,
    width: '800px',
  },
  flexColumns: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  formControl2: {
    flexBasis: '49%',
    margin: 0,
  },
  formControl3: {
    margin: '0 0 5px',
  },
  fullWidthInput: {
    width: '100%',
  },
  dropdownStyle: {
    '& li': {
      height: 48,
    },
  },
}));

type DialogUsersProps = {
  close: () => void;
  updateUserSelected: (user: User | null) => void;
  title: string;
  level?: string;
  code?: string;
  noResultsMessage: string;
  saveButtonLabel?: string;
};

const DialogUsers: React.FC<DialogUsersProps> = (props) => {
  const { close, updateUserSelected, title, level, code, noResultsMessage, saveButtonLabel } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const [resultsUsers, setResultsUsers] = useState<User[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [checkedAll, setCheckedAll] = useState(false);
  const [columnFilters, setColumnFilters] = useState({
    ...COLUMNS_DEFAULTS_FILTERS,
    userId: code,
  });
  const [selected, setSelected] = useState<User | null>(null);
  const [seletedHasChanged, setSelectedHasChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToastContext();

  const debouncedFilters = useDebouncedValue(columnFilters, 500);

  const mapUsers = (user) => {
    user.selected = code === user.userCode;

    if (user.selected) {
      setSelected(user);
    }

    return user;
  };

  const parseUsers = (res) => {
    const usersSelected = res.results.map(mapUsers);
    setResultsUsers(usersSelected);
    setCount(res.totalPages);
  };

  const getUsers = (pag, rows) => {
    usersServices.getUsers(pag, rows)
      .then(parseUsers)
      .catch((error) => addToast(error.message, 'ERROR'));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    getUsers(page, parseInt(event.target.value, 10));
    setPage(page);
  };

  const fetchUsersLevelFilterTable = useApiRequest(usersServices.getUsersLevelFilterTable);
  const fetchUsersFilterTable = useApiRequest(usersServices.getUsersFilterTable);

  useEffect(() => {
    setLoading(true);

    if (level) {
      fetchUsersLevelFilterTable(level, columnFilters, page, rowsPerPage)
        .then(parseUsers)
        .catch((error) => {
          if (error.name !== errorTypes.ABORT_ERROR) addToast(error.message, 'ERROR');
        })
        .finally(() => setLoading(false));
    } else {
      fetchUsersFilterTable(columnFilters, page, rowsPerPage)
        .then(parseUsers)
        .catch((error) => {
          if (error.name !== errorTypes.ABORT_ERROR) addToast(error.message, 'ERROR');
        })
        .finally(() => setLoading(false));
    }
  }, [debouncedFilters, page, level, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectAnotherUser = (element: User) => resultsUsers.map((currentUser: User) => {
    if (element.userCode === currentUser.userCode) {
      currentUser.selected = true;
      setSelected(currentUser);
    } else {
      currentUser.selected = false;
    }

    return currentUser;
  });

  const changeSelectedUserStatus = (element: User) => resultsUsers.map((currentUser: User) => {
    if (element.userCode === currentUser.userCode) {
      currentUser.selected = !currentUser.selected;
      setSelected(currentUser);
    }

    return currentUser;
  });

  const handleRowClick = (element: User) => {
    setSelectedHasChanged(true);
    const updatedUsers = (element.userCode !== selected?.userCode)
      ? selectAnotherUser(element)
      : changeSelectedUserStatus(element);
    setResultsUsers(updatedUsers);
  };

  const deselectRows = (rows, checked = false) => {
    rows.forEach((elem) => {
      elem.selected = checked;
    });
  };

  const handleCheckAll = () => {
    const users = [...resultsUsers];
    // Selecciona todos cuando queda alguno sin estar seleccionado
    // Deselecciona todos cuando todos están seleccionados.
    const selectAll = users.slice(0, users.length).some((elem) => !elem.selected);
    deselectRows(users.slice(0, users.length), selectAll);
    setCheckedAll(selectAll);
    setResultsUsers(users);
  };

  const handleFilterTable = (column, event) => {
    // filtramos la tabla según su columna por level o no
    setColumnFilters({ ...columnFilters, [column]: event.target.value });
    setPage(0);
  };

  const sendUsers = () => {
    updateUserSelected(selected);
    close();
  };

  const resetValues = useCallback(() => {
    setColumnFilters({ ...COLUMNS_DEFAULTS_FILTERS, userId: undefined });
  }, []);

  return (
    <Dialog
      open
      maxWidth="md"
      onClose={close}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{title}</DialogTitle>
      <DialogContent className={classes.content}>
        <div className={styles.DialogUsers}>
          <div className={styles.PageContent}>
            <div className={styles.ButtonsTable}>
              <div className={styles.ButtonsContentTableLeft}>
                <InputText
                  id="input-free-text"
                  handleChange={(ev) => handleFilterTable('allFields', ev)}
                  value={columnFilters?.allFields}
                  type="search"
                  icoSearch
                  maxLength={256}
                  label={t('search')}
                  size="small"
                  placeholder={t('dialog.filter')}
                  className={`${styles.FilterCell} ${columnFilters?.allFields !== '' ? styles.ActiveInputTextFilterCell : ''}`}
                />
              </div>
              <div className={styles.ButtonsContentTableRight}>
                <div className={styles.ButtonResetFilter} onClick={resetValues}>{t('dialog.resetFilter')}</div>
              </div>
            </div>
          </div>
        </div>
        <TableList
          isFilter
          list={resultsUsers}
          RowComponent={UserRow}
          handleRowClick={(element) => handleRowClick(element)}
          handleCheckAll={handleCheckAll}
          filterTable={(column, value) => handleFilterTable(column, value)}
          columnFilters={columnFilters}
          noResultsMessage={noResultsMessage}
          loading={loading}
        />
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={count * rowsPerPage}
          labelDisplayedRows={() => t('dialog.page', { page: page + 1, total: Math.floor(count) })}
          rowsPerPage={rowsPerPage}
          labelRowsPerPage={t('dialog.rowsPerPage')}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </DialogContent>
      <DialogActions>
        <Button
          id="dialog-users-ok"
          disabled={((selected && Object.keys(selected).length === 0) || !seletedHasChanged) && !checkedAll}
          variant="contained"
          color="primary"
          onClick={sendUsers}
        >
          {saveButtonLabel || t('dialog.save')}
        </Button>
        <Button id="dialog-users-cancel" color="primary" onClick={close}>{t('dialog.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default (DialogUsers);
